// 기능 안내 페이지(/guide) 데이터 — 원본 목록은 repo의 docs/FEATURES.md
// 새 기능을 출시하면 docs/FEATURES.md와 이 파일을 함께 갱신한다.
//
// action
//   { type: 'route', to }   → 해당 화면으로 이동
//   { type: 'metronome' }   → 메트로놈 시트 열기
//   없음                    → hint(위치 안내)만 표시
// newUntil: 'YYYY-MM-DD' 까지 NEW 뱃지 표시
import {
  EventAvailable, NotificationsActive, EventBusy, ConfirmationNumber,
  LibraryMusic, ViewWeek, AlarmOn, CalendarMonth, QueueMusic, MenuBook,
  Notifications, Tune, GetApp, DarkMode, Campaign, Swipe,
  FactCheck, History, TableChart, Dashboard, People, EventRepeat, Settings,
} from '@mui/icons-material';
import MetronomeIcon from '../metronome/MetronomeIcon';

export const FEATURE_CATEGORIES = ['레슨', '연습실', '연습 도구', '알림·앱', '관리'];

const MEMBER = ['MEMBER'];
const STAFF = ['ADMIN', 'TEACHER'];
const ADMIN = ['ADMIN'];
const ALL = ['MEMBER', 'TEACHER', 'ADMIN'];

export const FEATURES = [
  // ── 회원: 레슨
  { id: 'lesson-apply', roles: MEMBER, category: '레슨', icon: EventAvailable,
    title: '레슨 신청', desc: '날짜·시간·선생님을 골라 신청하면 승인 후 확정돼요',
    action: { type: 'route', to: '/lesson' } },
  { id: 'waitlist', roles: MEMBER, category: '레슨', icon: NotificationsActive,
    title: '빈자리 대기 알림', desc: '마감된 시간에 대기를 걸어두면 자리가 나는 즉시 알려드려요',
    action: { type: 'route', to: '/lesson' } },
  { id: 'lesson-cancel', roles: MEMBER, category: '레슨', icon: EventBusy,
    title: '레슨 취소 신청', desc: '확정된 레슨을 취소 신청하고, 필요하면 철회할 수 있어요',
    action: { type: 'route', to: '/myinfo' } },
  { id: 'tickets', roles: MEMBER, category: '레슨', icon: ConfirmationNumber,
    title: '티켓 확인', desc: '남은 티켓 수와 유효기간을 확인해요',
    action: { type: 'route', to: '/myinfo' } },

  // ── 회원: 연습실
  { id: 'practice-book', roles: MEMBER, category: '연습실', icon: LibraryMusic,
    title: '연습실 예약', desc: '날짜 → 연습실 → 시간 순서로 예약해요',
    action: { type: 'route', to: '/practice' } },
  { id: 'practice-overview', roles: MEMBER, category: '연습실', icon: ViewWeek,
    title: '전체 예약 현황', desc: '날짜별 모든 연습실의 시간표를 한눈에 봐요',
    action: { type: 'route', to: '/practice' } },
  { id: 'practice-auto', roles: MEMBER, category: '연습실', icon: AlarmOn,
    title: '레슨 전후 연습 자동 예약', desc: '평일 레슨 때 30분 연습이 자동으로 잡혀요. 레슨 전/후를 고를 수 있어요',
    action: { type: 'route', to: '/settings' } },

  // ── 회원: 연습 도구
  { id: 'my-schedule', roles: MEMBER, category: '연습 도구', icon: CalendarMonth,
    title: '내 일정', desc: '레슨·연습실 일정을 달력으로 모아 봐요',
    action: { type: 'route', to: '/my-schedule' } },
  { id: 'metronome', roles: MEMBER, category: '연습 도구', icon: MetronomeIcon, newUntil: '2026-12-31',
    title: '메트로놈', desc: '박자·강세·음성 카운트, 점점 빨라지는 속도 트레이너. 화면을 옮겨도 계속 울려요',
    action: { type: 'metronome' } },
  { id: 'piece-preset', roles: MEMBER, category: '연습 도구', icon: QueueMusic, newUntil: '2026-12-31',
    title: '내 곡 프리셋', desc: '자주 치는 곡의 템포와 박자를 저장해 한 번에 불러와요',
    action: { type: 'metronome' } },
  { id: 'journal', roles: MEMBER, category: '연습 도구', icon: MenuBook, newUntil: '2026-12-31',
    title: '연습 일지', desc: '곡별 연습 횟수·시간·메모를 기록하고 연속 연습일을 확인해요',
    action: { type: 'route', to: '/my-schedule?view=journal' } },

  // ── 회원: 알림·앱
  { id: 'bell', roles: MEMBER, category: '알림·앱', icon: Notifications,
    title: '알림함', desc: '빈자리·레슨 승인 소식을 모아 보고, 누르면 해당 화면으로 가요',
    hint: '상단 종 아이콘' },
  { id: 'push-settings', roles: ALL, category: '알림·앱', icon: Tune,
    title: '푸시 알림 설정', desc: '레슨 승인, 당일 레슨·연습실 알림 등을 항목별로 켜고 꺼요',
    action: { type: 'route', to: '/settings' } },
  { id: 'install', roles: ALL, category: '알림·앱', icon: GetApp, hideWhenInstalled: true,
    title: '홈 화면에 앱 설치', desc: '앱처럼 바로 열리고, 실시간 빈자리 알림도 받을 수 있어요',
    hint: '우측 상단 메뉴 → 앱 설치' },
  { id: 'dark-mode', roles: ALL, category: '알림·앱', icon: DarkMode,
    title: '다크 모드', desc: '어두운 화면으로 바꿔 눈의 피로를 줄여요',
    hint: '상단 해/달 아이콘' },
  { id: 'notice', roles: ALL, category: '알림·앱', icon: Campaign,
    title: '공지사항', desc: '학원 소식을 확인하고 댓글을 남겨요. 아래로 당기면 새로고침돼요',
    action: { type: 'route', to: '/notice' } },
  { id: 'calendar-tips', roles: ALL, category: '알림·앱', icon: Swipe,
    title: '달력 빠르게 넘기기', desc: '좌우로 밀어 달을 넘기고, 연·월을 눌러 바로 이동해요' },

  // ── 선생님·관리자
  { id: 'lesson-requests', roles: STAFF, category: '레슨', icon: FactCheck,
    title: '레슨 신청 현황', desc: '회원 레슨 신청과 빈자리 대기 현황을 확인해요',
    action: { type: 'route', to: '/lesson-requests' } },
  { id: 'activities', roles: STAFF, category: '레슨', icon: History,
    title: '변경사항', desc: '예약·취소 등 활동 내역을 달력으로 확인해요',
    action: { type: 'route', to: '/activities' } },
  { id: 'lesson-timetable', roles: STAFF, category: '레슨', icon: TableChart,
    title: '레슨 시간표', desc: '주간 레슨 시간표를 확인해요',
    action: { type: 'route', to: '/lesson' } },
  { id: 'practice-staff', roles: STAFF, category: '연습실', icon: LibraryMusic,
    title: '연습실 현황', desc: '연습실 예약 현황 확인, 대리 예약·차단 모드',
    action: { type: 'route', to: '/practice' } },

  // ── 관리자 전용
  { id: 'admin-dashboard', roles: ADMIN, category: '관리', icon: Dashboard,
    title: '대시보드', desc: '회원 추이와 통계 그래프',
    action: { type: 'route', to: '/admin' } },
  { id: 'admin-members', roles: ADMIN, category: '관리', icon: People,
    title: '회원 관리', desc: '결제 완료, 티켓 부여·이력, 레슨 이력, 비밀번호 초기화, 초성 검색',
    action: { type: 'route', to: '/admin' } },
  { id: 'admin-schedule', roles: ADMIN, category: '관리', icon: EventRepeat,
    title: '스케줄 · 플래너', desc: '고정 스케줄 지정, 변경 시점 지정, 충돌 미리보기',
    action: { type: 'route', to: '/admin' } },
  { id: 'admin-operations', roles: ADMIN, category: '관리', icon: Settings,
    title: '운영 설정', desc: '운영 시간·선생님 스케줄·휴일, 변경 영향 미리보기',
    action: { type: 'route', to: '/admin' } },
];

export const isFeatureNew = (f, today = new Date()) => {
  if (!f.newUntil) return false;
  return today <= new Date(`${f.newUntil}T23:59:59+09:00`);
};
